import {
	ParcelShopFromAPI,
	ParcelShop,
	ParcelShopHours,
	parcelShopFeaturesDefaults,
	ParcelShopIsOpen,
	FEATURE_COD,
	FEATURE_DROPOFFPOINT,
	FEATURE_ACCEPTS_CARD_PAYMENTS,
	FEATURE_ACCEPTS_CASH_PAYMENTS,
	FEATURE_GLS_PARCEL_LOCKER,
	FEATURE_OPEN_AFTER_8PM,
	FEATURE_OPEN_ON_WEEKENDS,
} from './parcel-shop';

const enum OpenCloseTimeType {
	Open,
	Close,
	BreakStart,
	BreakEnd,
}
const enum SortableOpenCloseTimeKeys {
	Key,
	Day,
	Hour,
	IsOpen,
	Type,
}

export type SortableOpenCloseTime = [key: string, day: number, hour: string, isOpen: boolean, type: OpenCloseTimeType];

export const transformParcelShop = (parcelShop: ParcelShopFromAPI): ParcelShop => {
	const hours: ParcelShopHours[] = parcelShop.hours.filter(([, open, close]) => typeof open === 'string' && typeof close === 'string');
	return {
		...parcelShop,
		hours,
		features: {
			...parcelShopFeaturesDefaults,
			// ...parcelShop.features,
			[FEATURE_COD]: parcelShop.features.isCodHandler,
			[FEATURE_DROPOFFPOINT]: parcelShop.features.dropoffpoint,
			[FEATURE_ACCEPTS_CARD_PAYMENTS]: parcelShop.features.isCodHandler && parcelShop.features.acceptsCard,
			[FEATURE_ACCEPTS_CASH_PAYMENTS]: parcelShop.features.isCodHandler && parcelShop.features.acceptsCash,
			[FEATURE_GLS_PARCEL_LOCKER]: parcelShop.features.isParcelLocker,
			[FEATURE_OPEN_AFTER_8PM]: parcelShop.hours?.some(h => h[2] > '20:00'),
			[FEATURE_OPEN_ON_WEEKENDS]: parcelShop.hours?.some(h => h[0] >= 6),
		},
		_sortedHours: hours
			.reduce((sortableOpenCloseTimes, hours) => {
				const [day, open, close, breakStart, breakEnd] = hours;

				if ([open, close, breakStart, breakEnd].some(hour => hour && !/^\d{1,2}:\d{2}$/.test(hour))) {
					console.log('invalid hours', parcelShop.id, hours);
					return sortableOpenCloseTimes;
				}
				const [openPadded, closePadded] = [open.padStart(5, '0'), close.padStart(5, '0')];

				if (openPadded && closePadded) {
					sortableOpenCloseTimes.push([`${day}_${openPadded}`, day, open, true, OpenCloseTimeType.Open], [`${day}_${closePadded}`, day, close, false, OpenCloseTimeType.Close]);

					if (breakStart && breakEnd) {
						const [breakStartPadded, breakEndPadded] = [breakStart.padStart(5, '0'), breakEnd.padStart(5, '0')];
						if (
							// sanity checks... (e.g.: 1122-CSOMAGPONT01)
							breakStartPadded > openPadded &&
							breakStartPadded < closePadded &&
							breakStartPadded < breakEndPadded &&
							breakEndPadded > openPadded &&
							breakEndPadded < closePadded &&
							breakEndPadded > breakStartPadded
						) {
							sortableOpenCloseTimes.push(
								[`${day}_${breakStartPadded}`, day, breakStart, false, OpenCloseTimeType.BreakStart],
								[`${day}_${breakEndPadded}`, day, breakEnd, true, OpenCloseTimeType.BreakEnd]
							);
						}
					}
				}
				return sortableOpenCloseTimes;
			}, [] as SortableOpenCloseTime[])
			.sort((a: SortableOpenCloseTime, b: SortableOpenCloseTime): number => {
				if (a[SortableOpenCloseTimeKeys.Key] < b[SortableOpenCloseTimeKeys.Key]) {
					return -1;
				} else if (a[SortableOpenCloseTimeKeys.Key] > b[SortableOpenCloseTimeKeys.Key]) {
					return +1;
				} else {
					return 0;
				}
			}),
	};
};

export const parcelShopIsOpen = (parcelShop: ParcelShop, time: [day: number, hour: string]): ParcelShopIsOpen => {
	let isOpen: ParcelShopIsOpen['isOpen'] = false;
	let willOpenAt: ParcelShopIsOpen['willOpenAt'];
	let willCloseAt: ParcelShopIsOpen['willCloseAt'];

	let nextChangeIndex: number;
	let nextChange: SortableOpenCloseTime;

	try {
		const nowKey = `${time[0]}_${time[1]}`;

		nextChangeIndex = parcelShop._sortedHours?.findIndex(h => h[SortableOpenCloseTimeKeys.Key] > nowKey);
		// if (parcelShop.id === '1122-CSOMAGPONT01') console.log(parcelShop.sortedHours, nowKey, nextChangeIndex);
		if (nextChangeIndex === -1) nextChangeIndex = 0;

		nextChange = parcelShop._sortedHours[nextChangeIndex];
		if (nextChange[SortableOpenCloseTimeKeys.IsOpen]) {
			isOpen = false;
			willOpenAt = [nextChange[SortableOpenCloseTimeKeys.Day], nextChange[SortableOpenCloseTimeKeys.Hour]];
		} else {
			isOpen = true;
			willCloseAt = [nextChange[SortableOpenCloseTimeKeys.Day], nextChange[SortableOpenCloseTimeKeys.Hour]];
		}
		// if (parcelShop.id === '1122-CSOMAGPONT01') console.log(nextChangeIndex, nextChange, isOpen, willOpenAt, willCloseAt);
		nextChangeIndex += 1;
		if (nextChangeIndex >= parcelShop._sortedHours.length) nextChangeIndex = 0;

		nextChange = parcelShop._sortedHours[nextChangeIndex];
		if (nextChange[SortableOpenCloseTimeKeys.IsOpen]) {
			willOpenAt = [nextChange[SortableOpenCloseTimeKeys.Day], nextChange[SortableOpenCloseTimeKeys.Hour]];
		} else {
			willCloseAt = [nextChange[SortableOpenCloseTimeKeys.Day], nextChange[SortableOpenCloseTimeKeys.Hour]];
		}
		// if (parcelShop.id === '1122-CSOMAGPONT01') console.log(nextChangeIndex, nextChange, isOpen, willOpenAt, willCloseAt);
		return {
			isOpen,
			willOpenAt,
			willCloseAt,
		};
	} catch (e) {
		// console.error(parcelShop, e);
		return {
			isOpen: false,
		};
	}
};
